import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { DEFAULT_TABLE_PAGE_SIZE } from 'src/constants/Pagination';
import TaskService from 'src/services/tasks/taskService';
import type { RegionTask, RegionTaskGroup, RegionTaskModification } from 'src/services/tasks/types';
import { type PickOne } from 'src/types/utilities';
import { arePrimitiveValuesDiff, arrayMove } from 'src/utils/helpers';
import { createTags, extractAvailableFields, filterByBooleans, filterByStrings } from 'src/utils/tableUtils';

import type { FilterTag, RegionForm, RegionFormTableRow, RestrictionFormTableFilter, TaskFormTableRow } from './types';

// **** Functions **** //

/**
 * Convert region object to a table row
 */
const regionFormToFormDataRow = (region: RegionForm): RegionFormTableRow => ({
  id: region.id,
  name: region.name,
  country: region.country?.name,
  formName: region.restrictionFormName == undefined ? `${region.name} Form` : region.restrictionFormName,
  isActive: region.restrictionFormActive == null ? false : region.restrictionFormActive,
  description: region.restrictionFormDescription,
});

/**
 * Setup the Region/Task Filter Options from the table data.
 */
const setupRestrictionFormTableFilterOptions = (tableData: RegionFormTableRow[]): RestrictionFormTableFilter => ({
  stateRegions: extractAvailableFields(tableData, 'name'),
  applicableCountries: extractAvailableFields(tableData, 'country'),
  formNames: extractAvailableFields(tableData, 'formName'),
  isActiveSelections: ['Yes', 'No'],
});

/**
 * Setup filtered table data using the JobConfig Table filter.
 */
const applyRestrictionFormTableFilterUtil = (
  tableData: RegionFormTableRow[],
  filter: RestrictionFormTableFilter,
): RegionFormTableRow[] => {
  let filteredData = cloneDeep(tableData);
  filteredData = filterByStrings(filteredData, 'name', filter.stateRegions);
  filteredData = filterByStrings(filteredData, 'country', filter.applicableCountries);
  filteredData = filterByStrings(filteredData, 'formName', filter.formNames);
  filteredData = filterByBooleans(
    filteredData,
    'isActive',
    filter.isActiveSelections.map((isActive) => isActive === 'Yes'),
  );
  return filteredData;
};

/**
 * Get the applied filter tabl from the JobConfig Table filter.
 */
const getRestrictionFormTableFilterTags = (filter: RestrictionFormTableFilter): FilterTag[] => [
  ...createTags(filter, 'stateRegions', 'State/Region/Province'),
  ...createTags(filter, 'applicableCountries', 'Country'),
  ...createTags(filter, 'formNames', 'Form Name'),
  ...createTags(filter, 'isActiveSelections', 'Is Active'),
];

/**
 * If a task doesn't have a rank, move it to the top, otherwise sort based on
 * rank ascending.
 */
const sortTasks = (t1: TaskFormTableRow, t2: TaskFormTableRow) => {
  if (t1.rank == null) {
    return -1;
  } else if (t2.rank == null) {
    return 1;
  } else {
    return t1.rank - t2.rank;
  }
};

/**
 * Change task order.
 */
const changeTaskOrder = (arr: TaskFormTableRow[], fromIndex: number, toIndex: number): TaskFormTableRow[] => {
  // If moved row don't have a rank, auto move to the first rank
  let updatedRank = 1;
  let updatedIndex = findHighestRankIndex(arr);
  if (arr[toIndex].rank !== null) {
    updatedRank = arr[toIndex].rank ?? 0;
    updatedIndex = toIndex;
  }
  arrayMove(arr, fromIndex, updatedIndex);
  // Update task rank from start index and rank all the way to the end
  arr[updatedIndex].rank = updatedRank;
  const startIndex = arr.findIndex((val) => val.rank !== null);
  let startRank = 1;
  arr.forEach((item, index) => {
    if (index >= startIndex) {
      item.rank = startRank++;
    }
  });
  // Return
  return arr;
};

/**
 * Corner case when there is no tasks has rank and move select task to
 * bottom.
 */
const findHighestRankIndex = (arr: TaskFormTableRow[]): number => {
  for (let i = 0; i < arr.length; i++) {
    if (arr[i].rank === 1) {
      return i - 1;
    }
  }
  return arr.length - 1;
};

/**
 * Get a list of all unique child-tasks for a group.
 */
const getSharedModifications = (group: RegionTaskGroup): RegionTaskModification[] => {
  if (group.tasks.length < 1) return [];
  let memo = new Set<number>();
  return group.tasks.reduce<RegionTaskModification[]>((acc, task) => {
    const toAdd = task.modifications.filter((m) => !memo.has(m.modificationId));
    memo = new Set([...memo, ...toAdd.map((m) => m.modificationId)]);
    return [...acc, ...toAdd];
  }, []);
};

/**
 * Check if there is any filter data.
 */
export const getHasRestrictionFormTableFilters = (filter: RestrictionFormTableFilter): boolean =>
  Object.values(filter).some((arr) => arr.length > 0);

// **** Setup Async-Thunks **** //

/**
 * Fetch regions
 */
export const fetchRegions = createAsyncThunk('regionTasks/fetchRegions', () => {
  return TaskService.fetchRegions();
});

/**
 * Fetch RegionTasks by region Id
 */
export const fetchRegionTasksByRegionId = createAsyncThunk('regionTasks/fetchRegionTasksByRegionId', (id: number) => {
  return TaskService.fetchRegionTasksByRegionId(id);
});

/**
 * Fetch RegionGroupsAndTasksDTO by region id
 */
export const fetchRegionGroupsAndTasksByRegionId = createAsyncThunk(
  'regionTasks/fetchRegionTasksAndGroupsByRegionId',
  (id: number) => {
    return TaskService.fetchRegionGroupsAndTasksByRegionId(id);
  },
);

/**
 * Update RegionTasks region id.
 */
export const updateRegionTasks = createAsyncThunk('regionTasks/updateRegionTasks', (tasks: RegionTask[]) => {
  return TaskService.updateRegionTasks(tasks);
});

/**
 * Get tasks of a region by region Id
 */
export const updateRestrictionForm = createAsyncThunk(
  'regionTasks/updateRestrictionForm',
  (formdata: RegionFormTableRow) => {
    return TaskService.updateRestrictionForm(formdata);
  },
);

// **** Setup Redux-Slice **** //

const initialState: RegionTaskPageState = {
  regionTableData: [],
  filteredRegionTableData: [],
  isFetchingRegions: false,
  currentPage: 1,
  totalPages: 0,
  perPage: DEFAULT_TABLE_PAGE_SIZE,
  searchField: '',
  regions: [],
  selectedRegion: {
    id: 0,
    name: '',
    country: '',
    formName: '',
    isActive: false,
    description: '',
  },
  taskFormTableData: [],
  selectedTaskFormTableRow: null,
  isRestrictionFormTableFilterOpen: false,
  restrictionFormTableFilterOptions: {
    stateRegions: [],
    applicableCountries: [],
    formNames: [],
    isActiveSelections: [],
  },
  restrictionFormTableFilter: {
    stateRegions: [],
    applicableCountries: [],
    formNames: [],
    isActiveSelections: [],
  },
  restrictionFormTableFilterTags: [],
};

interface RegionTaskPageState {
  regionTableData: RegionFormTableRow[];
  filteredRegionTableData: RegionFormTableRow[];
  isFetchingRegions: boolean;
  currentPage: number;
  totalPages: number;
  perPage: number;
  searchField: string;
  regions: RegionForm[];
  selectedRegion: RegionFormTableRow;
  taskFormTableData: TaskFormTableRow[];
  selectedTaskFormTableRow: TaskFormTableRow | null;
  isRestrictionFormTableFilterOpen: boolean;
  restrictionFormTableFilterOptions: RestrictionFormTableFilter;
  restrictionFormTableFilter: RestrictionFormTableFilter;
  restrictionFormTableFilterTags: FilterTag[];
}

const { reducer, actions } = createSlice({
  name: 'regionTasksConfigPageSlice',
  initialState,
  reducers: {
    setSearchField: (state, action) => {
      state.searchField = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setSelectedFormTasksTableRow: (state, action: PayloadAction<TaskFormTableRow | null>) => {
      state.selectedTaskFormTableRow = action.payload;
    },
    setFormTasksTableData: (state, action: PayloadAction<TaskFormTableRow[]>) => {
      state.taskFormTableData = action.payload;
    },
    setSelectedRegion: (state, action) => {
      state.selectedRegion = action.payload;
    },
    resetSeletedRegion: (state) => {
      state.selectedRegion = { ...initialState.selectedRegion };
    },
    changeTasksOrder: (state, action) => {
      const { old, new: new_ } = action.payload;
      state.taskFormTableData = changeTaskOrder(state.taskFormTableData, old, new_);
    },
    setIsRestrictionFormTableFilterSheetOpen: (state, action) => {
      state.isRestrictionFormTableFilterOpen = action.payload;
    },
    setRestrictionFormTableFilter: (state, action) => {
      state.restrictionFormTableFilter = action.payload;
    },
    setRestrictionFormTableFilterProp: (state, action: PayloadAction<PickOne<RestrictionFormTableFilter>>) => {
      state.restrictionFormTableFilter = {
        ...state.restrictionFormTableFilter,
        ...action.payload,
      };
    },
    applyRestrictionFormTableFilter: (state) => {
      state.filteredRegionTableData = applyRestrictionFormTableFilterUtil(
        state.regionTableData,
        state.restrictionFormTableFilter,
      );
      state.restrictionFormTableFilterTags = getRestrictionFormTableFilterTags(state.restrictionFormTableFilter);
    },
    resetRestrictionFormTableFilter: (state) => {
      state.restrictionFormTableFilter = {
        stateRegions: [],
        applicableCountries: [],
        formNames: [],
        isActiveSelections: [],
      };
    },
    removeTagAndUpdateFilter: (state, action) => {
      const newFilter = { ...state.restrictionFormTableFilter };
      const filterTag: FilterTag = action.payload;
      const { optionName, value } = filterTag;
      newFilter[optionName] = newFilter[optionName].filter((item) => arePrimitiveValuesDiff(item, value));
      state.restrictionFormTableFilter = newFilter;
      state.restrictionFormTableFilterTags = getRestrictionFormTableFilterTags(newFilter);
      state.filteredRegionTableData = applyRestrictionFormTableFilterUtil(state.regionTableData, newFilter);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRegions.pending, (state) => {
      state.isFetchingRegions = true;
    });
    builder.addCase(fetchRegions.fulfilled, (state, { payload }) => {
      state.regions = payload;
      const data = payload.map((region: any) => regionFormToFormDataRow(region));
      state.regionTableData = data;
      state.restrictionFormTableFilterOptions = setupRestrictionFormTableFilterOptions(data);
      state.isFetchingRegions = false;
      if (getHasRestrictionFormTableFilters(state.restrictionFormTableFilter)) {
        state.filteredRegionTableData = applyRestrictionFormTableFilterUtil(data, state.restrictionFormTableFilter);
        state.restrictionFormTableFilterTags = getRestrictionFormTableFilterTags(state.restrictionFormTableFilter);
      } else {
        state.filteredRegionTableData = [...data];
      }
    });
    builder.addCase(fetchRegions.rejected, (state) => {
      state.isFetchingRegions = false;
    });
    // TODO, remove this when taskGroup feature done
    builder.addCase(fetchRegionTasksByRegionId.fulfilled, (state, { payload }) => {
      const regionTasks: TaskFormTableRow[] = payload.map((task) => ({
        ...task,
        entityId: `t-${task.taskId}`,
        type: 'task',
      }));
      state.taskFormTableData = regionTasks.sort(sortTasks);
    });
    builder.addCase(fetchRegionGroupsAndTasksByRegionId.fulfilled, (state, { payload }) => {
      const taskGroups: TaskFormTableRow[] = payload.taskGroups.map((group) => {
        return {
          ...group,
          commonModifications: getSharedModifications(group),
          entityId: `g-${group.taskGroupId}`,
          type: 'task-group',
        };
      });
      const ungroupedTasks: TaskFormTableRow[] = payload.ungroupedTasks.map((task) => ({
        ...task,
        entityId: `t-${task.taskId}`,
        type: 'task',
      }));
      const allRows = [...taskGroups, ...ungroupedTasks];
      state.taskFormTableData = allRows.sort(sortTasks);
    });
  },
});

// **** Export **** //

export const {
  setSearchField,
  setCurrentPage,
  setPerPage,
  setTotalPages,
  setSelectedRegion,
  resetSeletedRegion,
  changeTasksOrder,
  setIsRestrictionFormTableFilterSheetOpen,
  setRestrictionFormTableFilter,
  setRestrictionFormTableFilterProp,
  applyRestrictionFormTableFilter,
  resetRestrictionFormTableFilter,
  removeTagAndUpdateFilter,
  setFormTasksTableData,
  setSelectedFormTasksTableRow,
} = actions;

export default reducer;
